import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePropertyComparison } from '@/hooks/usePropertyComparison';
import LanguageToggle from './LanguageToggle';
import NotificationBell from './NotificationBell';
import UserAvatar from './UserAvatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { User, Settings, LogOut, Building, Home, List, Heart, Scale, Menu, X, Sparkles } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
const Header = () => {
  const {
    user,
    profile,
    signOut
  } = useAuth();
  const {
    t,
    language,
    setLanguage
  } = useLanguage();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Use the property comparison hook
  const {
    comparisonList
  } = usePropertyComparison();
  
  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsMobileMenuOpen(false);
  };
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  return <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full max-w-none px-4 lg:px-6 xl:px-8">
        <div className="flex h-16 items-center justify-between w-full">
          {/* Logo and Navigation - positioned together on the left */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2" onClick={closeMobileMenu}>
              <Building className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold">Maskan</span>
            </Link>

            {/* Desktop Navigation - all items directly in header */}
            <nav className="hidden lg:flex items-center gap-6">
              <Link to="/search" className="text-sm font-medium transition-colors hover:text-primary">
                {t('search', 'البحث', 'Search')}
              </Link>
              
              <Link to="/list-property" className="text-sm font-medium transition-colors hover:text-primary">
                {t('list_property', 'أدرج عقارك', 'List Property')}
              </Link>
              
              {user && <Link to="/my-listings" className="text-sm font-medium transition-colors hover:text-primary">
                  {t('my_listings', 'عقاراتي', 'My Listings')}
                </Link>}
              
              <Link to="/about" className="text-sm font-medium transition-colors hover:text-primary">
                {t('about_us', 'من نحن', 'About')}
              </Link>
              
              <Link to="/contact" className="text-sm font-medium transition-colors hover:text-primary">
                {t('contact_us', 'اتصل بنا', 'Contact')}
              </Link>
            </nav>
          </div>

          {/* Right side - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageToggle currentLang={language} onToggle={setLanguage} />
            
            {user ? <div className="flex items-center gap-3">
                {/* Compare Icon with count from usePropertyComparison */}
                
                
                {/* Wishlist Icon */}
                <Button variant="ghost" size="icon" className="relative h-8 w-8 rounded-full" onClick={() => navigate('/wishlist')}>
                  <Heart className="h-4 w-4" />
                </Button>
                
                <NotificationBell />
                
                 <DropdownMenu>
                   <DropdownMenuTrigger asChild>
                     <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                       <UserAvatar 
                         src={profile?.avatar_url}
                         fullName={profile?.full_name}
                         size="md"
                       />
                     </Button>
                   </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <div className="flex items-center justify-start gap-2 p-2">
                      <div className="flex flex-col space-y-1 leading-none">
                        <p className="font-medium">{profile?.full_name || user.email}</p>
                        <p className="w-[200px] truncate text-sm text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem onClick={() => navigate('/account')}>
                      <Settings className="mr-2 h-4 w-4" />
                      {t('account', 'الحساب', 'Account')}
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem onClick={() => navigate('/compare')}>
                      <Scale className="mr-2 h-4 w-4" />
                      {t('compare', 'المقارنة', 'Compare')} {comparisonList.length > 0 && `(${comparisonList.length})`}
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem onClick={() => navigate('/wishlist')}>
                      <Heart className="mr-2 h-4 w-4" />
                      {t('wishlist', 'المفضلة', 'Wishlist')}
                    </DropdownMenuItem>
                    
                    {profile?.role !== 'student' && <DropdownMenuItem onClick={() => navigate('/my-listings')}>
                        <List className="mr-2 h-4 w-4" />
                        {t('my_listings', 'عقاراتي', 'My Listings')}
                      </DropdownMenuItem>}
                    
                    {profile?.role === 'admin' && <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Home className="mr-2 h-4 w-4" />
                        {t('admin_dashboard', 'لوحة الإدارة', 'Admin Dashboard')}
                      </DropdownMenuItem>}
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('sign_out', 'تسجيل الخروج', 'Sign Out')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div> : <div className="flex items-center gap-2">
                <Button variant="ghost" asChild>
                  <Link to="/signin">
                    {t('sign_in', 'تسجيل الدخول', 'Sign In')}
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/signup" className="!text-white">
                    {t('sign_up', 'إنشاء حساب', 'Sign Up')}
                  </Link>
                </Button>
              </div>}
          </div>

          {/* Mobile Menu Button and Language Toggle */}
          <div className="flex lg:hidden items-center gap-2">
            <LanguageToggle currentLang={language} onToggle={setLanguage} />
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0">
                <div className="flex flex-col h-full">
                  {/* User Profile Section - Now at the top */}
                   {user ? <div className="p-6 border-b">
                       <div className="flex items-center gap-3 mb-4">
                         <UserAvatar 
                           src={profile?.avatar_url}
                           fullName={profile?.full_name}
                           size="lg"
                           className="flex-shrink-0"
                         />
                         <div className="min-w-0 flex-1">
                           <p className="font-medium text-base truncate">{profile?.full_name || user.email}</p>
                           <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                         </div>
                       </div>
                      
                      {/* User Actions */}
                      <div className="space-y-2">
                        <Link to="/account" className="flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                          <Settings className="h-4 w-4" />
                          {t('account', 'الحساب', 'Account')}
                        </Link>
                        
                        <Link to="/compare" className="flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                          <Scale className="h-4 w-4" />
                          {t('compare', 'المقارنة', 'Compare')} {comparisonList.length > 0 && `(${comparisonList.length})`}
                        </Link>
                        
                        <Link to="/wishlist" className="flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                          <Heart className="h-4 w-4" />
                          {t('wishlist', 'المفضلة', 'Wishlist')}
                        </Link>
                        
                        {profile?.role !== 'student' && <Link to="/my-listings" className="flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                            <List className="h-4 w-4" />
                            {t('my_listings', 'عقاراتي', 'My Listings')}
                          </Link>}
                        
                        {profile?.role === 'admin' && <Link to="/admin" className="flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                            <Home className="h-4 w-4" />
                            {t('admin_dashboard', 'لوحة الإدارة', 'Admin Dashboard')}
                          </Link>}
                      </div>
                    </div> : <div className="p-6 border-b space-y-3">
                      <Link to="/signin" className="block px-4 py-3 text-sm font-medium text-center transition-colors hover:text-primary hover:bg-muted rounded-md border" onClick={closeMobileMenu}>
                        {t('sign_in', 'تسجيل الدخول', 'Sign In')}
                      </Link>
                      <Link to="/signup" className="block px-4 py-3 text-sm font-medium bg-primary !text-white hover:bg-primary/90 rounded-md text-center" onClick={closeMobileMenu}>
                        {t('sign_up', 'إنشاء حساب', 'Sign Up')}
                      </Link>
                    </div>}

                  {/* Navigation Links - Now below user section */}
                  <div className="flex-1 p-6">
                    <nav className="space-y-2">
                      <Link to="/search" className="flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                        {t('search', 'البحث', 'Search')}
                      </Link>
                      
                      <Link to="/list-property" className="flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                        {t('list_property', 'أدرج عقارك', 'List Property')}
                      </Link>
                      
                      <Link to="/about" className="flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                        {t('about_us', 'من نحن', 'About')}
                      </Link>
                      
                      <Link to="/contact" className="flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors hover:text-primary hover:bg-muted rounded-md" onClick={closeMobileMenu}>
                        {t('contact_us', 'اتصل بنا', 'Contact')}
                      </Link>
                    </nav>
                  </div>

                  {/* Sign Out - At the bottom */}
                  {user && <div className="p-6 border-t">
                      <button onClick={handleSignOut} className="flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors hover:text-destructive hover:bg-muted rounded-md w-full text-left">
                        <LogOut className="h-4 w-4" />
                        {t('sign_out', 'تسجيل الخروج', 'Sign Out')}
                      </button>
                    </div>}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
};
export default Header;